import { CuiIcon } from 'front-lib'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useFlag } from '@unleash/proxy-client-react'

import {
  FF_ENABLE_UPDATE_TO_STANDARD_PLAN_FEATURE,
  FF_ENABLE_RETURN_TO_WEEKLY_PLAN_FEATURE,
  FF_RETURN_TO_LAST_REGULAR_FROM_BOX_PLAN_FEATURE
} from 'src/constants/feature-flags'
import { SettingSection } from '../../../../components/SettingsCard'
import {
  isEconomicPlanSelector,
  isMonthlyPlanSelector,
  isBoxPlanSelector,
  getAllowPlanChange,
  isB2BUserSelector
} from 'src/redux-api-bridge/selectors'
import { UpgradeToStandardPlan } from '../UpgradeToStandardPlan/UpgradeToStandardPlan'
import { ReturnToWeeklyPlan } from '../ReturnToWeeklyPlan/ReturnToWeeklyPlan'
import { useMobileDevice } from '../../../../hooks/useMobileDevice'
import tracking from '../../../../shared/tracking'
import { usePlanConfiguration } from '../../hooks/usePlanConfiguration'
import { EconomicPlanTag } from '../EconomicPlanTag/EconomicPlanTag'
import { useChangePlanModal } from 'src/hooks/useChangePlanModal'
import { ReturnToLastRegularPlan } from '../ReturnToLastRegularPlan/ReturnToLastRegularPlan'
import { useExperiment } from 'src/hooks/useExperiment'
import { MYO_UPDATES } from 'src/constants/experiments'
import { SwitchToBoxPlanModal } from '../SwitchToBoxPlanModal/SwitchToBoxPlanModal'
import { useEffect, useState } from 'react'
import { useGetBoxPlanOffer } from '../../hooks/useGetBoxPlanOffer'
import { getUserId } from 'src/redux-api-bridge/account/selectors'

export function ChangePlanSettings() {
  const { t } = useTranslation()
  const isMobileDevice = useMobileDevice()
  const enableUpdateToStandardPlanFeature = useFlag(
    FF_ENABLE_UPDATE_TO_STANDARD_PLAN_FEATURE
  )
  const enableReturnToWeeklyPlanFeature = useFlag(
    FF_ENABLE_RETURN_TO_WEEKLY_PLAN_FEATURE
  )

  const enableReturnToLastRegularPlanFeature = useFlag(
    FF_RETURN_TO_LAST_REGULAR_FROM_BOX_PLAN_FEATURE
  )

  const { active } = useExperiment(MYO_UPDATES)

  const { open: openChangePlanModal } = useChangePlanModal()

  const {
    deliveriesPerWeek,
    pricePerMeal,
    isPlanWithDeliveryFee,
    planName
  } = usePlanConfiguration()

  const isMonthlyPlan = useSelector(isMonthlyPlanSelector)
  const isEconomicPlan = useSelector(isEconomicPlanSelector)
  const isBoxPlan = useSelector(isBoxPlanSelector)
  const allowPlanChange = useSelector(getAllowPlanChange)
  const isB2B = useSelector(isB2BUserSelector)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const userId = useSelector(getUserId)
  const {
    getBoxPlanOffer,
    boxPlanOffer,
    loading: isLoadingBoxPlanOffer,
    called: isCalledBoxPlanOffer
  } = useGetBoxPlanOffer()

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => setIsModalOpen(false)

  useEffect(() => {
    if (userId && !isBoxPlan) {
      getBoxPlanOffer({
        variables: { userId }
      })
    }
  }, [userId, getBoxPlanOffer, isBoxPlan])

  const showUpgrageToStandardPlan =
    enableUpdateToStandardPlanFeature && isEconomicPlan

  const showReturnToWeeklyPlan =
    enableReturnToWeeklyPlanFeature && isMonthlyPlan

  const showReturnToLastRegularPlan =
    enableReturnToLastRegularPlanFeature && isBoxPlan

  const hasValidBoxOfferData =
    boxPlanOffer?.payload?.boxSizePlanOfferModalTemplate?.boxes?.length > 0

  const isBoxPlanOfferAvailable = !isBoxPlan && hasValidBoxOfferData

  function onChangePlan() {
    tracking.track('Change Plan')
    openChangePlanModal()
  }

  const getPlanSectionProps = () => {
    return {
      className: 'cookunity__my_plan_section',
      title: (
        <span className="title_new_plan_meals">
          {planName}
          <span className="title_new_plan_meals_week">
            {' / '}
            {isPlanWithDeliveryFee
              ? t('pages.my_plan.week')
              : t('pages.my_plan.weekly')}
          </span>
          {active && <span className="tag-default-plan">DEFAULT PLAN</span>}
        </span>
      ),
      subtitle: isPlanWithDeliveryFee ? (
        <span>
          <span className="subtitle_new_plan_price">
            {`$${pricePerMeal} ${t('pages.my_plan.per_meal')}`}
          </span>
          <span className="subtitle_new_plan_delivery">
            {` + ${t('pages.my_plan.taxes_and_fees')}`}
          </span>
        </span>
      ) : (
        t('pages.my_plan.plan_subtitle', {
          pricePerMeal,
          deliveriesPerWeek,
          plural: deliveriesPerWeek > 1 ? 'ies' : 'y'
        })
      ),
      withActionButton: allowPlanChange,
      buttonLabel: (
        <>
          <CuiIcon name="sync" /> {t('pages.my_plan.change_plan')}
        </>
      )
    }
  }

  return (
    <>
      {isMobileDevice && isEconomicPlan && !isB2B && <EconomicPlanTag />}

      {showUpgrageToStandardPlan && !isB2B && <UpgradeToStandardPlan />}

      {showReturnToWeeklyPlan && !isB2B && <ReturnToWeeklyPlan />}

      {showReturnToLastRegularPlan && !isB2B && <ReturnToLastRegularPlan />}

      {isLoadingBoxPlanOffer && !isBoxPlan && isCalledBoxPlanOffer && (
        <SettingSection
          subtitle="Loading plan options..."
          onActionClick={onChangePlan}
        />
      )}

      {isBoxPlanOfferAvailable && !isLoadingBoxPlanOffer && (
        <>
          <SettingSection
            {...getPlanSectionProps()}
            buttonClassName="cookunity__my_plan_section-cancel-plan-secondary"
            onActionClick={onChangePlan}
            withSecondaryButton
            secondaryButtonLabel="Switch to Box Plan"
            onSecondaryClick={handleOpenModal}
          />
          <SwitchToBoxPlanModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            data={boxPlanOffer.payload.boxSizePlanOfferModalTemplate}
          />
        </>
      )}

      {!isBoxPlan && !isBoxPlanOfferAvailable && !isLoadingBoxPlanOffer && (
        <SettingSection
          {...getPlanSectionProps()}
          onActionClick={onChangePlan}
        />
      )}
    </>
  )
}
